import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {

  getDashboardEvaluationDetail,
  overrideDashboardEvaluationVerdict,
  resolveDashboardReview,
} from "../services/dashboardApi";
import { getSampleEvaluation, isSampleEvaluationId } from "../data/sampleDashboardData";

const verdictClasses = {
  ALLOW: "bg-success-focus text-success-main",
  BLOCK: "bg-danger-focus text-danger-main",
  REVIEW: "bg-warning-focus text-warning-main",
  LIMIT: "bg-warning-focus text-warning-main",
};

const riskClass = (score) => {
  if (score >= 70) return "text-danger-main";
  if (score >= 40) return "text-warning-main";
  return "text-success-main";
};

const score100 = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return null;
  }

  return number <= 1
    ? Math.round(number * 100)
    : Math.round(number);
};

const show = (value, fallback = "Unavailable") => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return fallback;
  }

  return String(value);
};

const yesNo = (value) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "Unavailable";
};

const percent = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "Unavailable";
  }

  const percentage =
    number <= 1
      ? number * 100
      : number;

  return `${Math.round(percentage)}%`;
};

const dateTime = (value) => {
  if (!value) {
    return "Unavailable";
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? "Unavailable"
    : date.toLocaleString();
};

const humanize = (value) =>
  String(value || "")
    .replace(/[._-]+/g, " ")
    .replace(
      /\b\w/g,
      (letter) =>
        letter.toUpperCase()
    );

const reasonLabel = (reason) => {
  const explanations = {
    domain_age_unknown:
      "Domain registration age could not be verified.",
    unknown_device:
      "This device has not been recognised previously.",
    insufficient_history:
      "Insufficient fingerprint history to establish a reliable baseline.",
  };

  return (
    explanations[reason] ||
    humanize(reason)
  );
};

const DetailRow = ({
  label,
  value,
  mono = false,
}) => (
  <div className="d-flex justify-content-between align-items-start gap-3 py-12 border-bottom">
    <span className="text-secondary-light flex-shrink-0">
      {label}
    </span>

    <span
      className="fw-medium text-end"
      style={{
        minWidth: 0,
        wordBreak: mono
          ? "break-all"
          : "break-word",
        ...(mono
          ? {
              fontFamily:
                "monospace",
            }
          : {}),
      }}
    >
      {value}
    </span>
  </div>
);

const SectionCard = ({
  title,
  icon,
  children,
}) => (
  <div className="card h-100">
    <div className="card-header border-bottom bg-base py-16 px-24">
      <div className="d-flex align-items-center gap-2">
        <Icon
          icon={icon}
          className="text-primary-600"
          width="20"
        />

        <h6 className="mb-0">
          {title}
        </h6>
      </div>
    </div>

    <div className="card-body p-24">
      {children}
    </div>
  </div>
);

const getSignal = (
  signals,
  ...keys
) => {
  for (const key of keys) {
    let value =
      signals?.[key];

    if (
      value === undefined &&
      key.includes(".")
    ) {
      value = key
        .split(".")
        .reduce(
          (current, part) =>
            current?.[part],
          signals
        );
    }

    if (
      value !== undefined &&
      value !== null &&
      value !== ""
    ) {
      return value;
    }
  }

  return null;
};

const EventDetailLayer = () => {
  const { eventId } =
    useParams();

  const navigate =
    useNavigate();

  const [event, setEvent] =
    useState(null);

  const [confirmAction, setConfirmAction] =
    useState(null);

  const [savingDecision, setSavingDecision] =
    useState(false);

  const [decisionError, setDecisionError] =
    useState("");

  const [loading, setLoading] =
    useState(true);

  const [
    loadError,
    setLoadError,
  ] = useState(null);

  useEffect(() => {
    let active = true;

    const loadEvent =
      async () => {
        setLoading(true);
        setLoadError(null);

        try {
          const response = isSampleEvaluationId(eventId)
            ? { evaluation: getSampleEvaluation(eventId) }
            : await getDashboardEvaluationDetail(eventId);

          if (!active) {
            return;
          }

          setEvent(
            response?.evaluation ||
              null
          );
        } catch (error) {
          if (!active) {
            return;
          }

          if (
            error?.status ===
            401
          ) {
            navigate(
              "/sign-in",
              {
                replace: true,
              }
            );

            return;
          }

          setLoadError(error);
        } finally {
          if (active) {
            setLoading(false);
          }
        }
      };

    loadEvent();

    return () => {
      active = false;
    };
  }, [eventId, navigate]);

  const riskScore =
    useMemo(() => {
      return score100(
        event?.score ??
          event?.finalScore ??
          event?.final_score ??
          event?.riskScore ??
          event?.risk_score
      );
    }, [event]);

  const reasons =
    useMemo(() => {
      const value =
        event?.reasonCodes ??
        event?.reason_codes ??
        event?.reasons ??
        [];

      return Array.isArray(
        value
      )
        ? value.filter(Boolean)
        : [];
    }, [event]);

  if (loading) {
    return (
      <div className="card">
        <div className="card-body p-40 text-center">
          <div
            className="spinner-border text-primary-600"
            role="status"
            aria-label="Loading evaluation"
          />

          <p className="text-secondary-light mt-16 mb-0">
            Loading evaluation
            details...
          </p>
        </div>
      </div>
    );
  }

  if (
    loadError ||
    !event
  ) {
    const notFound =
      loadError?.status ===
        404 ||
      loadError?.code ===
        "EVALUATION_NOT_FOUND";

    return (
      <>
        <div className="mb-24">
          <Link
            to="/dashboard"
            className="d-inline-flex align-items-center gap-2 text-primary-600 fw-medium"
          >
            <Icon icon="solar:arrow-left-outline" />
            Back to dashboard
          </Link>
        </div>

        <div className="card">
          <div className="card-body p-40 text-center">
            <Icon
              icon={
                notFound
                  ? "solar:document-text-outline"
                  : "solar:danger-triangle-outline"
              }
              className="text-secondary-light mb-16"
              width="42"
            />

            <h5 className="mb-8">
              {notFound
                ? "Evaluation not found"
                : "Unable to load evaluation"}
            </h5>

            <p className="text-secondary-light mb-24">
              {notFound
                ? "This evaluation does not exist or is not available to this account."
                : loadError?.message ||
                  "The evaluation details could not be loaded."}
            </p>

            <Link
              to="/dashboard"
              className="btn btn-primary-600"
            >
              Return to dashboard
            </Link>
          </div>
        </div>
      </>
    );
  }

  const verdict = String(
    event.verdict ??
      event.finalVerdict ??
      event.final_verdict ??
      event.ruleVerdict ??
      "UNKNOWN"
  ).toUpperCase();

  const signupEmail =
    event.signupEmail ||
    event.signup_email ||
    "Unavailable";

  const createdAt =
    event.createdAt ||
    event.created_at;

  const ipAddress =
    event.ipAddress ||
    event.ip_address ||
    event.ip ||
    null;

  const signals =
    event.signals &&
    typeof event.signals ===
      "object"
      ? event.signals
      : {};

  /*
   * Rules come only from the real
   * evaluation detail API.
   */
  const rules =
    Array.isArray(
      event.ruleMatches
    )
      ? event.ruleMatches
      : Array.isArray(
          event.rule_matches
        )
        ? event.rule_matches
        : [];

  /*
   * Review metadata is supplied by
   * the backend when an evaluation
   * has been through human review.
   */
  const manualReview =
    event.manualReview ||
    event.manual_review ||
    null;

  const manualLabel =
    verdict === "ALLOW" &&
    manualReview?.status ===
      "resolved" &&
    manualReview?.resolution ===
      "legitimate"
      ? "Manually approved"
      : verdict === "BLOCK" &&
          manualReview?.status ===
            "resolved" &&
          manualReview?.resolution ===
            "fraud"
        ? "Manually blocked"
        : null;

  const evaluationId =
    event.id ||
    event.evaluationId ||
    event.evaluation_id ||
    eventId ||
    "";

const shortEvaluationId =
  evaluationId
    ? String(evaluationId).slice(0, 8)
    : "Unavailable";

  const emailRows = [
    [
      "Domain",
      getSignal(
        signals,
        "email.domain"
      ),
      show,
    ],
    [
      "Domain age",
      getSignal(
        signals,
        "email.ageDays"
      ) ??
        getSignal(
          signals,
          "email.ageBasis"
        ),
      (value) =>
        value === "unknown"
          ? "Unknown"
          : Number.isFinite(
                Number(value)
              )
            ? `${value} days`
            : humanize(value),
    ],
    [
      "Disposable email",
      getSignal(
        signals,
        "email.disposable"
      ),
      yesNo,
    ],
    [
      "Free email provider",
      getSignal(
        signals,
        "email.freeProvider"
      ),
      yesNo,
    ],
    [
      "Suspicious domain extension",
      getSignal(
        signals,
        "email.suspiciousTld"
      ),
      yesNo,
    ],
    [
      "MX records",
      getSignal(
        signals,
        "email.hasMX"
      ),
      yesNo,
    ],
    [
      "SPF policy",
      getSignal(
        signals,
        "email.spfRecord"
      ),
      show,
    ],
    [
      "DMARC policy",
      getSignal(
        signals,
        "email.dmarcRecord"
      ),
      show,
    ],
  ];

  const networkRows = [
    [
      "IP address",
      ipAddress,
      show,
    ],
    [
      "Country",
      getSignal(
        signals,
        "network.country"
      ),
      (value) =>
        value === "GB"
          ? "United Kingdom"
          : value,
    ],
    [
      "Network provider",
      getSignal(
        signals,
        "network.asName"
      ),
      show,
    ],
    [
      "ASN",
      getSignal(
        signals,
        "network.asn"
      ),
      show,
    ],
    [
      "VPN / Proxy",
      getSignal(
        signals,
        "network.isVpnProxy"
      ),
      yesNo,
    ],
    [
      "Tor connection",
      getSignal(
        signals,
        "network.isTor"
      ),
      yesNo,
    ],
    [
      "Datacenter IP",
      getSignal(
        signals,
        "network.isDatacenter"
      ),
      yesNo,
    ],
    [
      "Threat listed",
      getSignal(
        signals,
        "network.isThreatListed"
      ),
      yesNo,
    ],
  ];

  const deviceRows = [
    [
      "Device identifier",
      getSignal(
        signals,
        "device.deviceId",
        "device.id"
      ),
      show,
    ],
    [
      "Device recognition",
      getSignal(
        signals,
        "device.known"
      ),
      (value) =>
        value
          ? "Previously recognised"
          : "Not recognised previously",
    ],
  ];

  const botRows = [
    [
      "Classification",
      getSignal(
        signals,
        "bot.classification"
      ),
      humanize,
    ],
    [
      "Bot score",
      getSignal(
        signals,
        "bot.botScore"
      ),
      percent,
    ],
    [
      "Honeypot triggered",
      getSignal(
        signals,
        "bot.honeypotTriggered"
      ),
      yesNo,
    ],
  ];

  const submitBlockedAllow = async () => {
    if (
      savingDecision ||
      !evaluationId
    ) {
      return;
    }

    try {
      setSavingDecision(true);
      setDecisionError("");

      if (isSampleEvaluationId(evaluationId)) {
        setEvent((current) => ({ ...current, verdict: "ALLOW" }));
        setConfirmAction(null);
        return;
      }

      await overrideDashboardEvaluationVerdict(
        evaluationId,
        "allow"
      );

      const response =
        await getDashboardEvaluationDetail(
          evaluationId
        );

      const refreshed =
        response?.evaluation ||
        response;

      setEvent(refreshed);
      setConfirmAction(null);
    } catch (error) {
      setDecisionError(
        error?.message ||
          "Unable to allow signup."
      );
    } finally {
      setSavingDecision(false);
    }
  };
  const submitReviewDecision = async () => {
    if (
      !confirmAction ||
      savingDecision ||
      !manualReview?.id
    ) {
      return;
    }

    const resolution =
      confirmAction === "approve"
        ? "legitimate"
        : "fraud";

    try {
      setSavingDecision(true);
      setDecisionError("");

      if (isSampleEvaluationId(evaluationId)) {
        setEvent((current) => ({
          ...current,
          verdict: resolution === "legitimate" ? "ALLOW" : "BLOCK",
          manualReview: {
            ...(current?.manualReview || {}),
            status: "resolved",
            resolution,
          },
        }));
        setConfirmAction(null);
        return;
      }

      await resolveDashboardReview(
        manualReview.id,
        {
          resolution,
          confidence: 1,
        }
      );

      const response =
        await getDashboardEvaluationDetail(
          evaluationId
        );

      const refreshed =
        response?.evaluation ||
        response;

      setEvent(refreshed);
      setConfirmAction(null);
    } catch (error) {
      setDecisionError(
        error?.message ||
          "Unable to resolve review."
      );
    } finally {
      setSavingDecision(false);
    }
  };

  const renderRows = (
    rows
  ) => {
    const available =
      rows.filter(
        ([, value]) =>
          value !== null &&
          value !== undefined &&
          value !== ""
      );

    if (!available.length) {
      return null;
    }

    return available.map(
      ([
        label,
        value,
        formatter,
      ]) => (
        <DetailRow
          key={label}
          label={label}
          value={formatter(
            value
          )}
          mono={
            label ===
              "IP address" ||
            label ===
              "Device identifier"
          }
        />
      )
    );
  };

  return (
    <>
      <div className="mb-24">
        <Link
          to="/events"
          className="d-inline-flex align-items-center gap-2 text-primary-600 fw-medium"
        >
          <Icon icon="solar:arrow-left-outline" />
          Back to events
        </Link>
      </div>

      {verdict === "REVIEW" &&
        manualReview?.id &&
        (manualReview?.status === "open" ||
          manualReview?.status === "in_review") && (
          <div className="card mb-24">
            <div className="card-header border-bottom bg-base py-16 px-24">
              <div className="d-flex align-items-center gap-2">
                <Icon
                  icon="solar:clipboard-check-outline"
                  className="text-warning-600"
                  width="20"
                />
                <h6 className="mb-0">
                  Review decision
                </h6>
              </div>
            </div>

            <div className="card-body p-24">
              <p className="text-secondary-light mb-20">
                Confirm whether this signup is legitimate or fraudulent.
              </p>

              {!confirmAction && (
                <div className="d-flex flex-wrap gap-3">
                  <button
                    type="button"
                    disabled={savingDecision}
                    className="btn btn-success-600 px-20 py-10"
                    onClick={() => {
                      setDecisionError("");
                      setConfirmAction("approve");
                    }}
                  >
                    <Icon
                      icon="solar:check-circle-outline"
                      className="me-2"
                    />
                    Approve Signup
                  </button>

                  <button
                    type="button"
                    disabled={savingDecision}
                    className="btn btn-danger-600 px-20 py-10"
                    onClick={() => {
                      setDecisionError("");
                      setConfirmAction("fraud");
                    }}
                  >
                    <Icon
                      icon="solar:shield-cross-outline"
                      className="me-2"
                    />
                    Mark as Fraud
                  </button>
                </div>
              )}

              {confirmAction && (
                <div className="border-top pt-20">
                  <div className="d-flex align-items-start gap-3 mb-20">
                    <span
                      className={`w-40-px h-40-px rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ${
                        confirmAction === "approve"
                          ? "bg-success-focus text-success-main"
                          : "bg-danger-focus text-danger-main"
                      }`}
                    >
                      <Icon
                        icon={
                          confirmAction === "approve"
                            ? "solar:check-circle-outline"
                            : "solar:shield-cross-outline"
                        }
                        width="22"
                      />
                    </span>

                    <div>
                      <h6 className="mb-6">
                        {confirmAction === "approve"
                          ? "Approve this signup?"
                          : "Mark this signup as fraud?"}
                      </h6>

                      <p className="text-secondary-light mb-0">
                        {confirmAction === "approve"
                          ? "The user will be allowed to continue signing in to your platform."
                          : "The signup will be blocked and recorded as fraud."}
                      </p>
                    </div>
                  </div>

                  <div className="d-flex flex-wrap gap-3">
                    <button
                      type="button"
                      disabled={savingDecision}
                      className={`btn ${
                        confirmAction === "approve"
                          ? "btn-success-600"
                          : "btn-danger-600"
                      } px-20 py-10`}
                      onClick={submitReviewDecision}
                    >
                      {savingDecision
                        ? "Saving..."
                        : confirmAction === "approve"
                          ? "Confirm Approval"
                          : "Confirm Fraud"}
                    </button>

                    <button
                      type="button"
                      disabled={savingDecision}
                      className="btn btn-outline-secondary px-20 py-10"
                      onClick={() => {
                        setConfirmAction(null);
                        setDecisionError("");
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {decisionError && (
                <div className="alert alert-danger mt-20 mb-0">
                  {decisionError}
                </div>
              )}
            </div>
          </div>
        )}

      {verdict === "BLOCK" && (
        <div className="card mb-24">
          <div className="card-body p-24">
            {confirmAction !== "allow-blocked" ? (
              <div className="d-flex justify-content-end">
                <button
                  type="button"
                  className="btn btn-success-600 px-20 py-10"
                  disabled={savingDecision}
                  onClick={() => {
                    setDecisionError("");
                    setConfirmAction("allow-blocked");
                  }}
                >
                  <Icon
                    icon="solar:check-circle-outline"
                    className="me-2"
                  />
                  Allow Signup
                </button>
              </div>
            ) : (
              <>
                <h6 className="mb-8">
                  Are you sure?
                </h6>

                <p className="text-secondary-light mb-20">
                  This signup is currently blocked.
                  Allowing it will manually override the decision.
                </p>

                <div className="d-flex flex-wrap gap-3">
                  <button
                    type="button"
                    className="btn btn-success-600 px-20 py-10"
                    disabled={savingDecision}
                    onClick={submitBlockedAllow}
                  >
                    {savingDecision
                      ? "Saving..."
                      : "Confirm Allow"}
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-secondary px-20 py-10"
                    disabled={savingDecision}
                    onClick={() => {
                      setConfirmAction(null);
                      setDecisionError("");
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}

            {decisionError && (
              <div className="alert alert-danger mt-20 mb-0">
                {decisionError}
              </div>
            )}
          </div>
        </div>
      )}
      <div className="card mb-24">
        <div className="card-body p-24">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-4">
            <div
              style={{
                minWidth: 0,
              }}
            >
              <span className="text-secondary-light text-sm">
                Signup evaluation
              </span>

              <h4
                className="mt-8 mb-12"
                style={{
                  wordBreak:
                    "break-word",
                }}
              >
                {signupEmail}
              </h4>

              <span className="text-secondary-light text-sm d-block">
                {dateTime(
                  createdAt
                )}
              </span>

              <span
                className="text-secondary-light text-sm d-block mt-4 font-monospace"
                title={
                  evaluationId
                }
              >
                Evaluation ID:{" "}
                {
                  shortEvaluationId
                }
              </span>
            </div>

            <div className="d-flex flex-wrap align-items-center justify-content-end gap-3">
              <div className="d-flex flex-column align-items-end gap-2">
                <span
                  className={`px-16 py-8 rounded-pill fw-semibold ${
                    verdictClasses[
                      verdict
                    ] ||
                    "bg-neutral-200 text-neutral-700"
                  }`}
                >
                  {verdict}
                </span>

                {manualLabel && (
                  <span className="text-secondary-light text-sm fw-semibold">
                    {manualLabel}
                  </span>
                )}
              </div>

              <div className="text-end ms-8">
                <span className="text-secondary-light text-sm d-block">
                  Risk score
                </span>

                <span
                  className={`text-xxl fw-bold ${
                    riskScore ===
                    null
                      ? ""
                      : riskClass(
                          riskScore
                        )
                  }`}
                >
                  {riskScore ===
                  null
                    ? "Unavailable"
                    : riskScore}

                  {riskScore !==
                  null
                    ? "/100"
                    : ""}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {reasons.length >
        0 && (
        <div className="card mb-24">
          <div className="card-header border-bottom bg-base py-16 px-24">
            <h6 className="mb-0">
              Why this evaluation
              received this result
            </h6>
          </div>

          <div className="card-body p-24">
            <div className="d-flex flex-wrap gap-2">
              {reasons.map(
                (
                  reason,
                  index
                ) => (
                  <div
                    className="d-inline-flex align-items-center gap-2 px-12 py-8 rounded-pill bg-warning-focus text-warning-main"
                    key={`${reason}-${index}`}
                  >
                    <Icon
                      icon="solar:alert-circle-outline"
                      width="18"
                    />

                    <span className="fw-medium">
                      {reasonLabel(
                        reason
                      )}
                    </span>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}

      <div className="row gy-4 mb-24">
        {[
          {
            title:
              "Email intelligence",
            icon: "solar:letter-outline",
            rows: emailRows,
          },
          {
            title:
              "Network intelligence",
            icon: "solar:global-outline",
            rows: networkRows,
          },
          {
            title:
              "Device intelligence",
            icon: "solar:smartphone-outline",
            rows: deviceRows,
          },
          {
            title:
              "Bot & behaviour",
            icon: "solar:shield-check-outline",
            rows: botRows,
          },
        ].map(
          (section) => {
            const content =
              renderRows(
                section.rows
              );

            if (!content) {
              return null;
            }

            return (
              <div
                className="col-xl-6"
                key={
                  section.title
                }
              >
                <SectionCard
                  title={
                    section.title
                  }
                  icon={
                    section.icon
                  }
                >
                  {content}
                </SectionCard>
              </div>
            );
          }
        )}
      </div>

      {rules.length > 0 && (
        <div className="card">
          <div className="card-header border-bottom bg-base py-16 px-24">
            <h6 className="mb-0">
              Matched rules
            </h6>
          </div>

          <div className="card-body p-24">
            {rules.map(
              (
                rule,
                index
              ) => (
                <div
                  className="border rounded p-16 mb-12"
                  key={
                    rule?.id ||
                    rule?.ruleId ||
                    index
                  }
                >
                  <div className="fw-semibold">
                    {show(
                      rule?.name ||
                        rule?.ruleName ||
                        rule?.ruleId,
                      `Rule ${
                        index + 1
                      }`
                    )}
                  </div>

                  {rule?.action && (
                    <div className="text-secondary-light text-sm mt-8">
                      Action:{" "}
                      {String(
                        rule.action
                      ).toUpperCase()}
                    </div>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default EventDetailLayer;
